import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Trash2, Camera, Sparkles, Send, X } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function ProgressPage() {
  const [weights, setWeights] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [bmi, setBmi] = useState(null);
  const [weight, setWeight] = useState("");
  const fileRef = useRef();
  const [tab, setTab] = useState("weight");
  const [coachOpen, setCoachOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    const [w, p, b] = await Promise.all([
      api.get("/progress/weight"),
      api.get("/progress/photos"),
      api.get("/progress/bmi"),
    ]);
    setWeights(w.data.logs);
    setPhotos(p.data.photos);
    setBmi(b.data);
  };
  useEffect(() => { load(); }, []);

  const addWeight = async (e) => {
    e.preventDefault();
    if (!weight) return;
    await api.post("/progress/weight", { weight_kg: parseFloat(weight) });
    setWeight("");
    await load();
    // trigger badge evaluation
    api.get("/progress/achievements").catch(() => {});
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      await api.post("/progress/photo", { image_data: reader.result, label: "progress" });
      await load();
    };
    reader.readAsDataURL(file);
  };

  const deletePhoto = async (id) => { await api.delete(`/progress/photo/${id}`); await load(); };

  const openCoach = async () => {
    setCoachOpen(true);
    const { data } = await api.get("/coach/history");
    setMessages(data.messages);
  };
  const sendCoach = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    const text = msg;
    setMsg("");
    setMessages((m) => [...m, { role: "user", content: text, id: `tmp-${Date.now()}` }]);
    try {
      const { data } = await api.post("/coach/message", { message: text });
      setMessages((m) => [...m.filter((x) => !x.id?.startsWith("tmp-")), data.user_message, data.assistant_message]);
      api.get("/progress/achievements").catch(() => {});
    } catch (e) {
      alert(e.response?.data?.detail || "Coach unavailable.");
    } finally {
      setSending(false);
    }
  };
  const clearCoach = async () => {
    await api.delete("/coach/history");
    setMessages([]);
  };

  const chartData = weights.map((w) => ({ date: w.date, kg: w.weight_kg }));

  return (
    <div className="fade-up">
      <p className="eyebrow">Progress · Proof</p>
      <h1 className="font-display font-black text-3xl md:text-5xl mt-2 brand-underline">
        TRACK THE REBUILD
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8 stagger">
        <div className="card">
          <p className="eyebrow">Weigh-ins</p>
          <p className="font-display font-black text-3xl mt-2">{weights.length}</p>
        </div>
        <div className="card">
          <p className="eyebrow">Current</p>
          <p className="font-display font-black text-3xl mt-2" style={{ color: "#FF0055" }}>
            {weights.length ? `${weights[weights.length - 1].weight_kg}` : "—"}
            <span className="text-sm text-zinc-500 ml-1">kg</span>
          </p>
        </div>
        <div className="card">
          <p className="eyebrow">BMI</p>
          <p className="font-display font-black text-3xl mt-2">{bmi?.bmi ?? "—"}</p>
          {bmi?.band && <p className="eyebrow mt-1 capitalize">{bmi.band}</p>}
        </div>
        <div className="card">
          <p className="eyebrow">Photos</p>
          <p className="font-display font-black text-3xl mt-2">{photos.length}</p>
        </div>
      </div>

      <div className="flex gap-2 mt-10 flex-wrap">
        {["weight", "photos", "coach"].map((t) => (
          <button
            key={t}
            onClick={() => (t === "coach" ? openCoach() : setTab(t))}
            data-testid={`tab-${t}`}
            className="px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors"
            style={{
              background: tab === t ? "#FF0055" : "transparent",
              color: tab === t ? "white" : "#a1a1aa",
              border: `1px solid ${tab === t ? "#FF0055" : "#3f3f46"}`,
            }}
          >
            {t === "coach" ? "AI Coach" : t}
          </button>
        ))}
      </div>

      {tab === "weight" && (
        <section className="mt-6">
          <form onSubmit={addWeight} className="card flex flex-wrap gap-3 items-end mb-6">
            <div className="flex-1 min-w-[180px]">
              <label className="label">Weight (kg)</label>
              <input className="input" type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="72.5" data-testid="weight-input"/>
            </div>
            <button className="btn-primary" data-testid="add-weight">Log weight</button>
          </form>

          {chartData.length > 1 && (
            <div className="card" style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A"/>
                  <XAxis dataKey="date" stroke="#71717A" fontSize={10}/>
                  <YAxis stroke="#71717A" fontSize={10} domain={["dataMin - 1", "dataMax + 1"]}/>
                  <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A" }}/>
                  <Line type="monotone" dataKey="kg" stroke="#FF0055" strokeWidth={2} dot={{ fill: "#FF0055", r: 3 }}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="mt-4 space-y-2">
            {weights.slice().reverse().map((w) => (
              <div key={w.id} className="card flex justify-between items-center">
                <div>
                  <p className="font-display font-bold">{w.weight_kg} kg</p>
                  <p className="text-xs text-zinc-500 mt-1 uppercase tracking-widest">{w.date}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "photos" && (
        <section className="mt-6">
          <div className="card mb-6 flex items-center gap-3">
            <input type="file" accept="image/*" ref={fileRef} onChange={uploadPhoto} className="hidden" data-testid="photo-input"/>
            <button className="btn-primary flex items-center gap-2" onClick={() => fileRef.current?.click()} data-testid="upload-photo">
              <Camera size={14}/> Add photo
            </button>
            <p className="text-xs text-zinc-500">Front, side, back. No pressure.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {photos.map((p) => (
              <div key={p.id} className="relative group" data-testid={`photo-${p.id}`}>
                <img src={p.image_data} alt="progress" className="w-full aspect-square object-cover" style={{ border: "1px solid #27272A" }}/>
                <button className="absolute top-2 right-2 p-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => deletePhoto(p.id)}>
                  <Trash2 size={14} color="#FF0055"/>
                </button>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">{p.date}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Coach modal */}
      {coachOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6" style={{ background: "rgba(0,0,0,0.7)" }}>
          <div className="w-full md:max-w-2xl h-[92vh] md:h-[80vh] flex flex-col" style={{ background: "#0A0A0A", border: "1px solid #27272A" }}>
            <div className="flex items-center justify-between p-4" style={{ borderBottom: "1px solid #27272A" }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center" style={{ background: "#FF0055" }}>
                  <Sparkles size={16} color="white"/>
                </div>
                <div>
                  <p className="font-display font-black uppercase text-sm">CINDR Coach</p>
                  <p className="text-[10px] eyebrow">Supportive, not soft</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="btn-ghost" onClick={clearCoach} data-testid="clear-coach">Clear</button>
                <button className="btn-ghost" onClick={() => setCoachOpen(false)} data-testid="close-coach"><X size={18}/></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-zinc-500 text-sm">
                  Ask the coach anything — low motivation, food noise, hard days. She meets you there.
                </div>
              )}
              {messages.map((m) => (
                <div key={m.id || m.created_at} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className="max-w-[85%] p-3 text-sm leading-relaxed"
                    style={{
                      background: m.role === "user" ? "#FF0055" : "#1A1A1A",
                      color: "white",
                      border: `1px solid ${m.role === "user" ? "#FF0055" : "#27272A"}`,
                    }}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && <div className="text-zinc-500 text-xs animate-pulse">Coach is typing…</div>}
            </div>
            <form onSubmit={sendCoach} className="p-3 flex gap-2" style={{ borderTop: "1px solid #27272A" }}>
              <input className="input flex-1" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type to your coach…" data-testid="coach-input"/>
              <button className="btn-primary flex items-center gap-2" disabled={sending} data-testid="coach-send"><Send size={14}/></button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
